import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, CardActionArea, Chip,
  LinearProgress, Alert, CircularProgress, Grid,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import learningPathService from '../../services/learningPathService';

const STATUS_COLORS = {
  creating: 'warning',
  in_progress: 'info',
  completed: 'success',
  abandoned: 'default',
};

const STATUS_LABELS = {
  creating: 'Creating',
  in_progress: 'In Progress',
  completed: 'Completed',
  abandoned: 'Abandoned',
};

const LearningPathList = () => {
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchPaths = async () => {
      try {
        const data = await learningPathService.getLearningPaths();
        if (!cancelled) {
          setPaths(Array.isArray(data) ? data : data.results || []);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load learning paths.');
          setLoading(false);
        }
      }
    };
    fetchPaths();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Learning Paths
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {paths.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <SchoolIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.primary">
            No learning paths yet
          </Typography>
          <Typography color="text.secondary">
            Use the Paper to Learning Path agent in a course to create your first personalized learning path.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {paths.map((path) => (
            <Grid item xs={12} sm={6} md={4} key={path.id}>
              <Card variant="outlined">
                <CardActionArea onClick={() => navigate(`/learning-paths/${path.id}`)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1 }}>
                        {path.title}
                      </Typography>
                      <Chip
                        label={STATUS_LABELS[path.status] || path.status}
                        color={STATUS_COLORS[path.status] || 'default'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>

                    {path.source_document_title && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        From: {path.source_document_title}
                      </Typography>
                    )}

                    {path.progress && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {path.progress.completed}/{path.progress.total} steps
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={path.progress.percentage}
                          sx={{ borderRadius: 1 }}
                          aria-label={`Progress: ${path.progress.completed} of ${path.progress.total} steps completed`}
                        />
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default LearningPathList;
